import React from 'react';
import { Skeleton } from '@material-ui/lab';
import { Typography, makeStyles } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import { useGetProjectRoleReferencedStatusInProjectRateTable } from './hooks';

export const useStyles = makeStyles(theme => ({
  warningSkeleton: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 'auto',
    marginLeft: theme.spacing(1),
    height: theme.spacing(2.5),
    width: theme.spacing(12)
  },
  iconOnlyWarningSkeleton: {
    marginLeft: theme.spacing(1),
    width: theme.spacing(3.5)
  },
  warningContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5, 0.5, 1.5),
    marginRight: 'auto',
    marginLeft: theme.spacing(1),
    height: 'fit-content',
    backgroundColor: theme.palette.warning.lightBackground
  },
  warningIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.warning.dark
  },
  warningIconOnly: {
    marginLeft: theme.spacing(1),
    color: theme.palette.warning.dark
  },
  warningText: {
    color: theme.palette.warning.standard
  }
}));

const resources = {
  task: {
    withRole: {
      tooltipTitle: (
        <FormattedMessage id="taskResourceEstimates.taskRoleNotOnRateCardTooltip" />
      ),
      warningMessage: (
        <FormattedMessage id="taskResourceEstimates.taskRoleNotOnRateCard" />
      )
    },
    withoutRole: {
      tooltipTitle: (
        <FormattedMessage id="taskResourceEstimates.taskRoleNotSelectedTooltip" />
      ),
      warningMessage: (
        <FormattedMessage id="taskResourceEstimates.taskRoleNotSelected" />
      )
    }
  },
  estimate: {
    withRole: {
      tooltipTitle: (
        <FormattedMessage id="taskResourceEstimates.roleNotOnRateCardTooltip" />
      ),
      warningMessage: (
        <FormattedMessage id="taskResourceEstimates.roleNotOnRateCard" />
      )
    },
    withoutRole: {
      tooltipTitle: (
        <FormattedMessage id="taskResourceEstimates.roleNotSelectedTooltip" />
      ),
      warningMessage: (
        <FormattedMessage id="taskResourceEstimates.roleNotSelected" />
      )
    }
  }
};

export const ProjectRoleReferenceInfoOnProjectRateTable = ({
  projectId,
  taskUri,
  projectRoleId,
  variant = 'Full',
  resourceEstimate = false
}) => {
  const classes = useStyles();

  const {
    projectRoleReferencedInProjectRateTable,
    loading
  } = useGetProjectRoleReferencedStatusInProjectRateTable({
    projectId,
    roleUri: projectRoleId,
    taskUri,
    resourceEstimateSummary: variant === 'IconOnly' && resourceEstimate
  });

  const resourceRootKey = resourceEstimate ? 'estimate' : 'task';

  const resourceKey = projectRoleReferencedInProjectRateTable?.roleUri
    ? 'withRole'
    : 'withoutRole';

  if (loading)
    return (
      <Skeleton
        className={
          variant === 'IconOnly'
            ? classes.iconOnlyWarningSkeleton
            : classes.warningSkeleton
        }
        variant="rect"
      />
    );

  if (variant === 'IconOnly')
    return (
      <>
        {projectRoleReferencedInProjectRateTable &&
        !projectRoleReferencedInProjectRateTable?.isReferenced ? (
          <Tooltip title={resources[resourceRootKey][resourceKey].tooltipTitle}>
            <WarningAmberIcon className={classes.warningIconOnly} />
          </Tooltip>
        ) : null}
      </>
    );

  return (
    <>
      {projectRoleReferencedInProjectRateTable &&
      !projectRoleReferencedInProjectRateTable?.isReferenced ? (
        <Tooltip title={resources[resourceRootKey][resourceKey].tooltipTitle}>
          <div className={classes.warningContainer}>
            <WarningAmberIcon className={classes.warningIcon} />
            <Typography className={classes.warningText} variant="body2">
              {resources[resourceRootKey][resourceKey].warningMessage}
            </Typography>
          </div>
        </Tooltip>
      ) : null}
    </>
  );
};

ProjectRoleReferenceInfoOnProjectRateTable.propTypes = {
  projectId: PropTypes.string.isRequired,
  taskUri: PropTypes.string,
  projectRoleId: PropTypes.string,
  variant: PropTypes.oneOf(['IconOnly', 'Full']),
  resourceEstimate: PropTypes.bool
};
